import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaHome, FaMapMarkerAlt, FaChartBar, FaUsers, FaMapMarkedAlt,
  FaCog, FaSignOutAlt, FaChevronLeft, FaChevronRight,
  FaSun, FaMoon, FaUserCircle, FaUserShield, FaUserPlus, FaClipboardList, FaSitemap, FaExclamationTriangle, FaTasks
} from 'react-icons/fa';
import { jwtDecode } from 'jwt-decode';
import { getCurrentUser, hasPermission } from '../../utils/permissionUtils';
import notificationService from '../../services/notificationService';
import websocketService from '../../services/websocketService';
import './ModernSidebar.css';

const ModernSidebar = ({ role }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('fieldflow-theme') || 'dark';
  });
  const [userInfo, setUserInfo] = useState({
    name: 'User',
    role: 'Personnel',
    avatar: null
  });

  // Get user info from token
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token) {
        const decoded = jwtDecode(token);

        let userName = 'User';
        if (storedUser) {
          try {
            const userObj = JSON.parse(storedUser);
            userName = userObj.name || userObj.username || userObj.email || userName;
          } catch (e) {
            // Silent error handling
          }
        }

        // Fallback to token if localStorage user doesn't have name
        if (userName === 'User') {
          userName = decoded.name || decoded.username || decoded.email || decoded.userId || 'User';
        }

        setUserInfo({
          name: userName,
          role: decoded.role === 'admin' ? 'Administrator' :
            decoded.role === 'sub-admin' ? 'Administrator' :
              decoded.role === 'super-admin' ? 'Super Admin' : 'Field Personnel',
          avatar: decoded.avatar || null
        });
      }
    } catch (error) {
      // Silent error handling
    }
  }, []);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('fieldflow-theme', theme);
  }, [theme]);

  // Handle window resize for tablet view
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 768 && width < 1024) {
        setCollapsed(true);
      } else if (width >= 1024) {
        setCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  const handleLogout = () => {
    // Clear notification service state
    notificationService.clear();
    // Disconnect WebSocket
    websocketService.disconnect();
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
  };

  // Navigation items based on role
  const getNavItems = () => {
    const user = getCurrentUser();

    if (role === 'super-admin') {
      return [
        {
          path: '/superadmin',
          label: 'Dashboard',
          icon: FaHome,
          active: location.pathname === '/superadmin' || location.pathname === '/superadmin/'
        },
        {
          path: '/superadmin/company-admins',
          label: 'Company Management',
          icon: FaUsers,
          adminOnly: true,
          active: location.pathname.includes('/superadmin/company-admins')
        },
        {
          path: '/superadmin/company-admins/new',
          label: 'Add Company',
          icon: FaMapMarkedAlt,
          adminOnly: true,
          active: location.pathname === '/superadmin/company-admins/new'
        }
      ];
    }

    if (role === 'admin') {
      const isFullAdmin = user?.role === 'admin';
      const isSubAdmin = user?.role === 'sub-admin';

      const navItems = [
        {
          path: '/admin',
          label: 'Dashboard',
          icon: FaHome,
          active: location.pathname === '/admin' || location.pathname === '/admin/legacy'
        }
      ];

      // Statistics - check permission for sub-admins
      if (isFullAdmin || hasPermission(user, 'statistics', 'view')) {
        navItems.push({
          path: '/admin/statistics',
          label: 'Statistics',
          icon: FaChartBar,
          active: location.pathname === '/admin/statistics'
        });
      }

      // Personnel Management - check permission for sub-admins
      if (isFullAdmin || hasPermission(user, 'personnel', 'view')) {
        navItems.push({
          path: '/admin/personnel',
          label: 'Personnel Management',
          icon: FaUsers,
          active: location.pathname === '/admin/personnel'
        });
      }

      // Sub-Admin Management - check permission for sub-admins with full privileges
      if (isFullAdmin || hasPermission(user, 'subAdmins', 'view')) {
        navItems.push({ 
          path: '/admin/subadmins', 
          label: 'Admin Management', 
          icon: FaUserShield,
          adminOnly: true,
          active: location.pathname === '/admin/subadmins'
        });
      }

      // Site Management - check permission for sub-admins
      if (isFullAdmin || hasPermission(user, 'sites', 'view')) {
        navItems.push({
          path: '/admin/sites',
          label: 'Site Management',
          icon: FaMapMarkedAlt,
          active: location.pathname === '/admin/sites'
        });
      }

      // Structure Management - check permission for sub-admins
      if (isFullAdmin || hasPermission(user, 'structureManagement', 'access')) {
        navItems.push({
          path: '/admin/structure',
          label: 'Structure Management',
          icon: FaSitemap,
          active: location.pathname === '/admin/structure'
        });
      }

      // Coverage - check permission for sub-admins
      if (isFullAdmin || hasPermission(user, 'coverage', 'view')) {
        navItems.push({
          path: '/admin/coverage',
          label: 'Coverage',
          icon: FaClipboardList,
          active: location.pathname === '/admin/coverage'
        });
      }

      // New Clients - check permission for sub-admins
      if (isFullAdmin || hasPermission(user, 'newClients', 'view')) {
        navItems.push({
          path: '/admin/clients',
          label: 'New Clients',
          icon: FaUserPlus,
          active: location.pathname === '/admin/clients'
        });
      }

      // Incident Reports - check permission for sub-admins
      if (isFullAdmin || hasPermission(user, 'incidentReports', 'access')) {
        navItems.push({
          path: '/admin/incident-reports',
          label: 'Incident Reports',
          icon: FaExclamationTriangle,
          active: location.pathname === '/admin/incident-reports'
        });
      }

      // Special Assignments - check permission for sub-admins
      if (isFullAdmin || hasPermission(user, 'specialAssignments', 'access')) {
        navItems.push({
          path: '/admin/special-assignments',
          label: 'Special Assignments',
          icon: FaTasks,
          active: location.pathname === '/admin/special-assignments'
        });
      }

      return navItems;
    }

    // Personnel navigation (includes Check In/Out)
    return [
      {
        path: `/${role}`,
        label: 'Dashboard',
        icon: FaHome,
        active: location.pathname === `/${role}` || location.pathname === `/${role}/legacy`
      },
      {
        path: `/${role}/check`,
        label: 'Check In/Out',
        icon: FaMapMarkerAlt,
        badge: 'live',
        active: location.pathname === `/${role}/check`
      },
      {
        path: `/${role}/coverage`,
        label: 'Coverage',
        icon: FaClipboardList,
        active: location.pathname === `/${role}/coverage`
      },
      {
        path: `/${role}/statistics`,
        label: 'My Statistics',
        icon: FaChartBar,
        active: location.pathname === `/${role}/statistics`
      },
      {
        path: `/${role}/clients`,
        label: 'New Clients',
        icon: FaUserPlus,
        active: location.pathname === `/${role}/clients`
      },
      {
        path: `/${role}/incident-report`,
        label: 'Incident Report',
        icon: FaExclamationTriangle,
        active: location.pathname === `/${role}/incident-report`
      },
      {
        path: `/${role}/special-assignments`,
        label: 'Special Assignments',
        icon: FaTasks,
        active: location.pathname === `/${role}/special-assignments`
      }
    ];
  };

  const navItems = getNavItems();

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="mobile-overlay"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar Container */}
      <aside
        className={`modern-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
      >
        {/* Logo Section */}
        <div className="sidebar-header">
          <Link to={role === 'super-admin' ? '/superadmin' : `/${role}`} className="logo-container" onClick={closeMobileMenu}>
            <div className="logo-wrapper">
              <span className="logo-text">FieldFlow</span>
            </div>
          </Link>

          {/* Desktop Collapse Toggle */}
          <button
            className="collapse-toggle desktop-only"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="sidebar-nav">
          {navItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className={`nav-item ${item.active ? 'active' : ''} ${item.adminOnly ? 'admin-item' : ''}`}
              onClick={closeMobileMenu}
              title={collapsed ? item.label : ''}
            >
              <div className="nav-icon-wrapper">
                <item.icon className="nav-icon" />
                {item.badge === 'live' && (
                  <span className="live-pulse" title="Active session"></span>
                )}
              </div>
              {!collapsed && (
                <>
                  <span className="nav-label">{item.label}</span>
                  {item.badge === 'live' && (
                    <span className="badge-live">LIVE</span>
                  )}
                </>
              )}
            </Link>
          ))}

          {/* Settings */}
          <Link
            to={role === 'super-admin' ? '/superadmin/settings' : `/${role}/settings`}
            className={`nav-item ${location.pathname === (role === 'super-admin' ? '/superadmin/settings' : `/${role}/settings`) ? 'active' : ''}`}
            onClick={closeMobileMenu}
            title={collapsed ? 'Settings' : ''}
          >
            <div className="nav-icon-wrapper">
              <FaCog className="nav-icon" />
            </div>
            {!collapsed && <span className="nav-label">Settings</span>}
          </Link>
        </nav>

        {/* Bottom Section */}
        <div className="sidebar-footer">
          {/* Theme Toggle */}
          <button
            className="theme-toggle-sidebar"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <div className="theme-toggle-track">
              <div className={`theme-toggle-thumb ${theme === 'light' ? 'light' : ''}`}>
                {theme === 'dark' ? <FaMoon size={12} /> : <FaSun size={12} />}
              </div>
            </div>
            {!collapsed && (
              <span className="theme-toggle-label">
                {theme === 'dark' ? 'Dark' : 'Light'} Mode
              </span>
            )}
          </button>

          {/* User Profile Section */}
          <div className="user-section">
            <div className="user-profile-display">
              <div className="user-avatar">
                {userInfo.avatar ? (
                  <img src={userInfo.avatar} alt={userInfo.name} />
                ) : (
                  <FaUserCircle size={collapsed ? 32 : 40} />
                )}
              </div>
              {!collapsed && (
                <div className="user-info">
                  <span className="user-name">{userInfo.name}</span>
                  <span className="user-role">{userInfo.role}</span>
                </div>
              )}
            </div>

            {/* Logout Button */}
            <button
              className={`logout-button ${collapsed ? 'collapsed' : ''}`}
              onClick={handleLogout}
              title="Logout"
            >
              <FaSignOutAlt />
              {!collapsed && <span>Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        {role === 'super-admin' ? (
          <>
            <Link
              to="/superadmin"
              className={`mobile-nav-item ${location.pathname === '/superadmin' || location.pathname === '/superadmin/' ? 'active' : ''}`}
            >
              <FaHome size={20} />
              <span>Dashboard</span>
            </Link>

            <Link
              to="/superadmin/company-admins"
              className={`mobile-nav-item ${location.pathname.includes('/superadmin/company-admins') ? 'active' : ''}`}
            >
              <FaUsers size={20} />
              <span>Companies</span>
            </Link>

            <Link
              to="/superadmin/company-admins/new"
              className={`mobile-nav-item ${location.pathname === '/superadmin/company-admins/new' ? 'active' : ''}`}
            >
              <FaMapMarkedAlt size={20} />
              <span>Add</span>
            </Link>

            <Link
              to="/superadmin/settings"
              className={`mobile-nav-item ${location.pathname === '/superadmin/settings' ? 'active' : ''}`}
            >
              <FaCog size={20} />
              <span>Settings</span>
            </Link>
          </>
        ) : role === 'admin' ? (
          <>
            {(() => {
              const user = getCurrentUser();
              const isFullAdmin = user?.role === 'admin';
              const mobileItems = [];

              // Dashboard always shown
              mobileItems.push(
                <Link
                  key="dashboard"
                  to="/admin"
                  className={`mobile-nav-item ${location.pathname === '/admin' ? 'active' : ''}`}
                >
                  <FaHome size={20} />
                  <span>Dashboard</span>
                </Link>
              );

              // Coverage - check permission for sub-admins
              if (isFullAdmin || hasPermission(user, 'coverage', 'view')) {
                mobileItems.push(
                  <Link
                    key="coverage"
                    to="/admin/coverage"
                    className={`mobile-nav-item ${location.pathname === '/admin/coverage' ? 'active' : ''}`}
                  >
                    <FaClipboardList size={20} />
                    <span>Coverage</span>
                  </Link>
                );
              }

              // Statistics - check permission for sub-admins
              if (isFullAdmin || hasPermission(user, 'statistics', 'view')) {
                mobileItems.push(
                  <Link
                    key="statistics"
                    to="/admin/statistics"
                    className={`mobile-nav-item ${location.pathname === '/admin/statistics' ? 'active' : ''}`}
                  >
                    <FaChartBar size={20} />
                    <span>Stats</span>
                  </Link>
                );
              }

              // Settings always shown
              mobileItems.push(
                <Link
                  key="settings"
                  to="/admin/settings"
                  className={`mobile-nav-item ${location.pathname === '/admin/settings' ? 'active' : ''}`}
                >
                  <FaCog size={20} />
                  <span>Settings</span>
                </Link>
              );

              return mobileItems;
            })()}
          </>
        ) : (
          <>
            <Link
              to={`/${role}`}
              className={`mobile-nav-item ${location.pathname === `/${role}` ? 'active' : ''}`}
            >
              <FaHome size={20} />
              <span>Dashboard</span>
            </Link>

            <Link
              to={`/${role}/check`}
              className={`mobile-nav-item ${location.pathname === `/${role}/check` ? 'active' : ''}`}
            >
              <FaMapMarkerAlt size={20} />
              <span>Check In</span>
              {location.pathname === `/${role}/check` && (
                <span className="mobile-active-dot"></span>
              )}
            </Link>

            <Link
              to={`/${role}/coverage`}
              className={`mobile-nav-item ${location.pathname === `/${role}/coverage` ? 'active' : ''}`}
            >
              <FaClipboardList size={20} />
              <span>Coverage</span>
            </Link>

            <Link
              to={`/${role}/settings`}
              className={`mobile-nav-item ${location.pathname === `/${role}/settings` ? 'active' : ''}`}
            >
              <FaCog size={20} />
              <span>Settings</span>
            </Link>
          </>
        )}
      </nav>

      {/* Floating Action Button (Mobile Check-In) - Only for personnel */}
      {role === 'personnel' && location.pathname !== `/${role}/check` && (
        <Link
          to={`/${role}/check`}
          className="mobile-fab"
          aria-label="Quick Check In"
        >
          <FaMapMarkerAlt size={24} />
        </Link>
      )}
    </>
  );
};

export default ModernSidebar;
